import React, { useState } from 'react';
import {
  Card,
  CardActionArea,
  Box,
  Typography,
  Chip,
  Collapse,
  Button,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleIcon from '@mui/icons-material/People';
import ClassificacaoBadge, { getClassificacaoColor, getPercentualColor } from './ClassificacaoBadge';
import ProgressBarWithMetas from './ProgressBarWithMetas';
import { isEscalaInvertida, C1_THRESHOLDS } from '../../utils/saude-classificacao';

interface EquipeIndicador {
  indicador: string;
  nome?: string;
  numerador: number;
  denominador: number;
  percentual: number;
  pontuacao?: number;
  peso?: number;
  valor?: number;
  unidade?: 'percent' | 'ratio';
  classificacao?: string;
}

/** Texto do valor: valor real (% ou razao) quando disponivel, senao o percentual */
const indicadorValorTexto = (ind: EquipeIndicador): string =>
  (ind.valor !== undefined && ind.valor !== null)
    ? (ind.unidade === 'ratio'
        ? ind.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : `${ind.valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`)
    : `${ind.percentual.toFixed(1)}%`;

interface EquipeCardData {
  ine: string;
  equipe: string;
  unidade: string;
  total_identificados: number;
  indicadores: EquipeIndicador[];
  score: number;
  classificacao: string;
}

interface EquipeCardProps {
  equipe: EquipeCardData;
  /** Eixo em exibicao: define a escala de cor do indicador (o C1 tem escala invertida). */
  eixo?: string;
  indicadoresNomes?: Record<string, string>;
  onVerPacientes?: (ine: string) => void;
  defaultExpanded?: boolean;
}

const getIndicadorLabel = (indicador: string): string => {
  const parts = indicador.split('_');
  return parts[parts.length - 1]?.toUpperCase() || indicador;
};

const EquipeCard: React.FC<EquipeCardProps> = ({
  equipe,
  eixo,
  indicadoresNomes,
  onVerPacientes,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const escalaInvertida = isEscalaInvertida(eixo);

  /**
   * Cor do indicador: pela classificacao propria (faixas por indicador, ex: odonto);
   * nos eixos de escala invertida (C1), pela classificacao da equipe - exato, pois o
   * eixo tem indicador unico cujo percentual e o proprio score (senao 85% sairia
   * verde ao lado do selo vermelho "Regular"); nos demais, pelo percentual.
   */
  const indicadorColor = (ind: EquipeIndicador): string =>
    ind.classificacao
      ? getClassificacaoColor(ind.classificacao)
      : escalaInvertida
        ? getClassificacaoColor(equipe.classificacao)
        : getPercentualColor(ind.percentual);

  // Eixos de indicador unico com faixa (eSB B1-B6, eMulti M1/M2): o "score" e apenas
  // faixa x peso, redundante com o selo de classificacao ao lado. Exibir o valor atingido
  // (ex.: 76,9% para percentual, 1,15 para razao) em vez do valor da faixa.
  const isIndicadorUnicoBandado =
    equipe.indicadores.length === 1 && equipe.indicadores[0]?.valor != null;
  const valorPrincipal = isIndicadorUnicoBandado
    ? indicadorValorTexto(equipe.indicadores[0])
    : equipe.score.toFixed(1);

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      {/* Collapsed header */}
      <CardActionArea onClick={() => setExpanded((prev) => !prev)} sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Left: name + unidade + count */}
          <Box sx={{ minWidth: 160, flex: '0 0 auto' }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {equipe.equipe}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {equipe.unidade}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              {equipe.total_identificados} identificados
            </Typography>
          </Box>

          {/* Center: indicator chips */}
          <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
            {equipe.indicadores.map((ind) => {
              const label = getIndicadorLabel(ind.indicador);
              const color = indicadorColor(ind);
              return (
                <Chip
                  key={ind.indicador}
                  label={`${label}:${indicadorValorTexto(ind)}`}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 22,
                    backgroundColor: alpha(color, 0.1),
                    color,
                    fontWeight: 600,
                  }}
                />
              );
            })}
          </Box>

          {/* Right: badge + score + expand icon */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <ClassificacaoBadge classificacao={equipe.classificacao} variant="chip" size="small" />
            <Typography variant="body2" fontWeight={700}>
              {valorPrincipal}
            </Typography>
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.25s ease',
                color: 'text.secondary',
              }}
            />
          </Box>
        </Box>
      </CardActionArea>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          {/* Indicators list */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {equipe.indicadores.map((ind) => {
              const nome = indicadoresNomes?.[ind.indicador] || ind.nome || getIndicadorLabel(ind.indicador);
              const color = indicadorColor(ind);
              // "faltam" (den - num) nao faz sentido nos eixos de escala invertida (C1): fechar a
              // lacuna ate o denominador NAO e o objetivo - acima de 70% a equipe volta a ser
              // Regular. A Visao Geral do eixo ja omite esse texto pelo mesmo motivo.
              const faltam = ind.denominador - ind.numerador;
              return (
                <Box key={ind.indicador}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                    <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1 }}>
                      {nome}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color, ml: 1 }}>
                      {indicadorValorTexto(ind)}
                    </Typography>
                  </Box>
                  <ProgressBarWithMetas
                    value={ind.percentual}
                    color={color}
                    height={6}
                    thresholds={escalaInvertida ? C1_THRESHOLDS : undefined}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                    {ind.numerador}/{ind.denominador}
                    {!escalaInvertida && <> &mdash; faltam {faltam > 0 ? faltam : 0}</>}
                  </Typography>
                </Box>
              );
            })}

            {onVerPacientes && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PeopleIcon />}
                onClick={() => onVerPacientes(equipe.ine)}
                sx={{ mt: 1, alignSelf: 'flex-start' }}
              >
                Ver pacientes desta equipe
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
};

export default EquipeCard;
